import { useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import { Alert } from '@material-ui/lab';
import { Autocomplete } from '@material-ui/lab';
import { TabProps, TenantConfiguratorVariant, Team } from './types';
import { useUserTeams } from '../shared';
import { useUserProfile } from './useUserProfile';

interface OwnershipTabProps extends TabProps {
  owner: string;
  variant: TenantConfiguratorVariant;
}

const PERMISSIONS = ['read', 'triage', 'write', 'maintain', 'admin'] as const;

const PURPOSE_OPTIONS = [
  { value: 'sandbox', label: 'Sandbox' },
  { value: 'demo', label: 'Demo Environment' },
  { value: 'internal', label: 'Internal' },
  { value: 'research', label: 'Research and Development' },
  { value: 'client', label: 'Client Testing' },
  { value: 'platform', label: 'Badal Platform' },
] as const;

export function OwnershipTab({ config, onChange, owner, variant }: OwnershipTabProps) {
  const { teams: userTeams, loading: teamsLoading } = useUserTeams(owner);
  const { displayName, email, loading: profileLoading } = useUserProfile();

  const nameInitialized = useRef(false);
  const emailInitialized = useRef(false);

  // Auto-populate ONLY in sandbox mode
  useEffect(() => {
    if (variant !== 'sandbox') return;
    if (!profileLoading && displayName && !nameInitialized.current && !config.owner_name) {
      nameInitialized.current = true;
      onChange({ ...config, owner_name: displayName });
    }
  }, [profileLoading, displayName, variant]);

  useEffect(() => {
    if (variant !== 'sandbox') return;
    if (!profileLoading && email && !emailInitialized.current && !config.owner_email) {
      emailInitialized.current = true;
      onChange({ ...config, owner_email: email });
    }
  }, [profileLoading, email, variant]);

  // Auto-set purpose to 'sandbox' in sandbox mode
  useEffect(() => {
    if (variant === 'sandbox' && config.purpose !== 'sandbox') {
      onChange({ ...config, purpose: 'sandbox' });
    }
  }, [variant]);

  // Auto-populate owner_team from first team
  useEffect(() => {
    if (config.teams.length > 0 && !config.owner_team) {
      const firstTeam = config.teams[0];
      if (firstTeam.name) {
        onChange({ ...config, owner_team: firstTeam.name });
      }
    }
  }, [config.teams]);

  const handleAddTeam = () => {
    const newTeam: Team = { name: '', permission: 'maintain' };
    onChange({ ...config, teams: [...config.teams, newTeam] });
  };

  const handleRemoveTeam = (index: number) => {
    onChange({ ...config, teams: config.teams.filter((_, i) => i !== index) });
  };

  const handleTeamNameChange = (index: number, slug: string | null) => {
    const updated = config.teams.map((t, i) =>
      i === index ? { ...t, name: slug ?? '' } : t,
    );
    const newConfig = { ...config, teams: updated };
    if (index === 0 && slug && !config.owner_team) {
      newConfig.owner_team = slug;
    }
    onChange(newConfig);
  };

  const handleTeamPermissionChange = (index: number, permission: Team['permission']) => {
    const updated = config.teams.map((t, i) =>
      i === index ? { ...t, permission } : t,
    );
    onChange({ ...config, teams: updated });
  };

  // Validation
  const isSandbox = variant === 'sandbox';
  const purposeMissing = !isSandbox && !config.purpose;
  const ownerNameMissing = !isSandbox && !config.owner_name;
  const ownerEmailMissing = !isSandbox && !config.owner_email;
  const maintainerMissing = !config.teams.some(
    t => t.name && t.permission === 'maintain',
  );
  const hasErrors = purposeMissing || ownerNameMissing || ownerEmailMissing || maintainerMissing;

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 24 }}>
      {hasErrors && (
        <Alert severity="warning">
          User must review project ownership and purpose before continuing.
        </Alert>
      )}

      <TextField
        label="Owner Name"
        variant="outlined"
        fullWidth
        required={!isSandbox}
        error={ownerNameMissing}
        value={config.owner_name}
        onChange={e => onChange({ ...config, owner_name: e.target.value })}
        helperText={
          ownerNameMissing
            ? 'Required - first and last name'
            : isSandbox
              ? 'Auto-filled from your profile'
              : undefined
        }
      />

      <TextField
        label="Owner Email"
        variant="outlined"
        fullWidth
        required={!isSandbox}
        error={ownerEmailMissing}
        value={config.owner_email}
        onChange={e => onChange({ ...config, owner_email: e.target.value })}
        helperText={
          ownerEmailMissing
            ? 'Required — enter owner email'
            : isSandbox
              ? 'Auto-filled from your profile'
              : undefined
        }
      />

      <TextField
        label="Cost Center"
        variant="outlined"
        fullWidth
        value={config.cost_center}
        onChange={e => onChange({ ...config, cost_center: e.target.value })}
        helperText="Optional — enter a cost center code for billing attribution"
      />

      <TextField
        label="Owner Team"
        variant="outlined"
        fullWidth
        value={config.owner_team}
        onChange={e => onChange({ ...config, owner_team: e.target.value })}
        helperText="Auto-filled from the first team below. Edit freely."
      />

      <FormControl variant="outlined" fullWidth error={purposeMissing} required={!isSandbox}>
        <InputLabel id="purpose-label">Purpose</InputLabel>
        <Select
          labelId="purpose-label"
          value={config.purpose}
          onChange={e => onChange({ ...config, purpose: e.target.value as string })}
          label="Purpose"
          disabled={isSandbox}
        >
          {PURPOSE_OPTIONS.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </Select>
        {purposeMissing && (
          <FormHelperText>Required — select a purpose for this tenant</FormHelperText>
        )}
        {isSandbox && (
          <FormHelperText>Automatically set to Sandbox</FormHelperText>
        )}
      </FormControl>

      <Divider />

      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
        <Typography
          variant="subtitle1"
          style={maintainerMissing ? { color: '#d32f2f' } : undefined}
        >
          Teams
        </Typography>
        {maintainerMissing && (
          <Typography variant="caption" style={{ color: '#d32f2f' }}>
            (at least one team with maintain permission required)
          </Typography>
        )}
      </Box>
      <Typography variant="body2" color="textSecondary">
        Select from your GitHub teams to grant repository access. At least one
        team must have maintain permission so the repository can be
        administered.
      </Typography>

      {config.teams.map((team, index) => (
        <Box key={index} display="flex" alignItems="center" style={{ gap: 12 }}>
          <Autocomplete
            options={userTeams.map(t => t.slug)}
            getOptionLabel={(slug) => {
              const found = userTeams.find(t => t.slug === slug);
              return found ? found.name : slug;
            }}
            loading={teamsLoading}
            value={team.name || null}
            onChange={(_, value) => handleTeamNameChange(index, value)}
            style={{ flex: 1 }}
            renderOption={(slug) => {
              const found = userTeams.find(t => t.slug === slug);
              return (
                <Box>
                  <Typography variant="body2">{found?.name ?? slug}</Typography>
                  <Typography variant="caption" color="textSecondary">{slug}</Typography>
                </Box>
              );
            }}
            renderInput={params => (
              <TextField {...params} label="Team" variant="outlined" size="small" />
            )}
          />
          <FormControl variant="outlined" size="small" style={{ minWidth: 130 }}>
            <Select
              value={team.permission}
              onChange={e => handleTeamPermissionChange(index, e.target.value as Team['permission'])}
            >
              {PERMISSIONS.map(p => (
                <MenuItem key={p} value={p}>{p}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton edge="end" aria-label="remove team" onClick={() => handleRemoveTeam(index)} size="small">
            <DeleteIcon />
          </IconButton>
        </Box>
      ))}
      <Button
        variant="outlined"
        color={maintainerMissing ? 'secondary' : 'primary'}
        startIcon={<AddIcon />}
        onClick={handleAddTeam}
        style={maintainerMissing ? { borderColor: '#d32f2f', color: '#d32f2f' } : undefined}
      >
        Add Team
      </Button>
    </Box>
  );
}
